import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

const AddNewSetModal = ({
  isOpen,
  onClose,
  onAddSet,
  newSetTitle,
  setNewSetTitle,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}) => {
  // State for time selection
  const [startTime, setStartTime] = useState({
    hour: "12",
    minute: "00",
    period: "AM",
  });
  const [endTime, setEndTime] = useState({
    hour: "12",
    minute: "00",
    period: "AM",
  });

  if (!isOpen) return null;

  // Function to combine date and time while preserving local time
  const combineDateAndTime = (date, time) => {
    const newDate = new Date(date);
    let hours = parseInt(time.hour, 10);
    const minutes = parseInt(time.minute, 10);

    // Convert to 24-hour format
    if (time.period === "PM" && hours !== 12) {
      hours += 12;
    } else if (time.period === "AM" && hours === 12) {
      hours = 0;
    }

    newDate.setHours(hours, minutes, 0, 0);
    return newDate;
  };

  // Function to convert date to ISO string without timezone conversion
  const toLocalISOString = (date) => {
    const pad = (n) => (n < 10 ? '0' + n : n);
    return date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate()) +
      'T' + pad(date.getHours()) +
      ':' + pad(date.getMinutes()) +
      ':' + pad(date.getSeconds()) +
      '.000Z'; // We add Z to indicate UTC, but the time is actually local
  };

  const handleAddSetWithTime = () => {
    if (!newSetTitle || !startDate || !endDate) {
      toast.error("Please provide a title, start date, and end date.");
      return;
    }

    // Combine date and time for start and end dates
    const startDateTime = combineDateAndTime(startDate, startTime);
    const endDateTime = combineDateAndTime(endDate, endTime);

    // Validate that end date/time is after start date/time
    if (endDateTime <= startDateTime) {
      toast.error("End date/time must be after start date/time");
      return;
    }

    // Call the onAddSet function with the dates in local time ISO format
    onAddSet({
      setCode: "",
      setTitle: newSetTitle,
      startDate: toLocalISOString(startDateTime),
      endDate: toLocalISOString(endDateTime),
      products: [],
    });
  };


  return (
    <div className="fixed inset-0 flex justify-center items-center ">
      <div className="bg-gray-100 rounded-2xl w-1/3 shadow-2xl">
        {/* Header */}
        <div className="flex rounded-t-2xl flex-row justify-between items-center px-6 py-4 bg-blue-600">
          <h2 className="text-xl font-bold font-roboto text-white">
            Add New Section
          </h2>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="w-9 h-9 rounded-full flex justify-center items-center transition hover:bg-blue-700"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="mt-4">
            <label className="font-roboto text-md text-gray-900 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={newSetTitle}
              onChange={(e) => setNewSetTitle(e.target.value)}
              placeholder="Enter new set title"
              className="border border-gray-300 p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
            />
          </div>

          {/* Start Date and Time */}
          <div className="mt-4">
            <label className="font-roboto text-md text-gray-900 mb-2">
              Start Date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              placeholderText="Start Date"
              className="border border-gray-300 p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
            />
            <div className="flex space-x-2 mt-2">
              <select
                value={startTime.hour}
                onChange={(e) =>
                  setStartTime({ ...startTime, hour: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <select
                value={startTime.minute}
                onChange={(e) =>
                  setStartTime({ ...startTime, minute: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i < 10 ? `0${i}` : i}>
                    {i < 10 ? `0${i}` : i}
                  </option>
                ))}
              </select>
              <select
                value={startTime.period}
                onChange={(e) =>
                  setStartTime({ ...startTime, period: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>

          {/* End Date and Time */}
          <div className="mt-4">
            <label className="font-roboto text-md text-gray-900 mb-2">
              End Date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              placeholderText="End Date"
              minDate={startDate}
              className="border border-gray-300 p-3 w-full rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
            />
            <div className="flex space-x-2 mt-2">
              <select
                value={endTime.hour}
                onChange={(e) =>
                  setEndTime({ ...endTime, hour: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <select
                value={endTime.minute}
                onChange={(e) =>
                  setEndTime({ ...endTime, minute: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={i < 10 ? `0${i}` : i}>
                    {i < 10 ? `0${i}` : i}
                  </option>
                ))}
              </select>
              <select
                value={endTime.period}
                onChange={(e) =>
                  setEndTime({ ...endTime, period: e.target.value })
                }
                className="border border-gray-300 p-2 rounded-lg"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 flex justify-start bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleAddSetWithTime}
            className="w-56 py-3 font-roboto  bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg focus:ring-4 focus:outline-none focus:ring-blue-300 transition-all"
          >
            Add Section
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewSetModal;